/**
 * Catálogo admin a partir da API (Dynamo). Fonte de verdade na leitura após login.
 */

import { adminIngestApiUrl } from './adminIngestApi';
import type { AdminSyncPayload } from './adminSyncApi';
import { adminApiHeaders } from './adminApiAuth';

const CATALOG_TIMEOUT_MS = 90_000;

export const ADMIN_CATALOG_HYDRATED_EVENT = 'naviex-admin-catalog-hydrated';

export type FetchAdminCatalogResult =
  | { ok: true; data: AdminSyncPayload; partial?: boolean }
  | { ok: false; error: string };

function parsePayload(raw: unknown): AdminSyncPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  return raw as AdminSyncPayload;
}

/** URL de GET /api/admin/catalog (conforme `VITE_API_URL` / `VITE_ADMIN_SYNC_URL`). */
export function resolveAdminCatalogFetchUrl(): string {
  return adminIngestApiUrl('/api/admin/catalog');
}

function humanizeCatalogNetworkError(message: string, requestUrl: string): string {
  const m = message.trim().toLowerCase();
  if (m === 'failed to fetch' || m === 'load failed' || m.includes('networkerror')) {
    return [
      'O browser não conseguiu falar com a API (rede / DNS / CORS).',
      'Se no .env.local ainda aparece "xxxxxxxx" no URL, isso é só um exemplo da documentação AWS — substitua pelo ID real do API Gateway (copie da consola AWS: API Gateway → Estágios → URL de invocação, só a origem, sem /api).',
      'Outras causas: domínio errado, API offline, ou CORS sem localhost.',
      `Pedido: ${requestUrl}`,
    ].join(' ');
  }
  return message;
}

export async function fetchAdminCatalogFromApi(): Promise<FetchAdminCatalogResult> {
  const controller = new AbortController();
  const kill = setTimeout(() => controller.abort(), CATALOG_TIMEOUT_MS);
  const url = resolveAdminCatalogFetchUrl();
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: adminApiHeaders(),
      signal: controller.signal,
    });
    const text = await res.text();
    let json: unknown;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      return { ok: false, error: text || `Resposta inválida (${res.status})` };
    }

    if (res.status === 207 && json && typeof json === 'object' && 'catalog' in json) {
      const data = parsePayload((json as { catalog: unknown }).catalog);
      if (data) return { ok: true, data, partial: true };
    }

    if (!res.ok) {
      const err =
        json && typeof json === 'object' && 'message' in json
          ? String((json as { message: unknown }).message)
          : text || `Erro ${res.status}`;
      return { ok: false, error: err };
    }

    const data = parsePayload(json);
    if (!data) return { ok: false, error: 'Formato de catálogo inválido.' };
    return { ok: true, data };
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return { ok: false, error: `Tempo esgotado (${CATALOG_TIMEOUT_MS / 1000}s).` };
    }
    const raw = err instanceof Error ? err.message : 'Erro ao conectar com a API';
    return { ok: false, error: humanizeCatalogNetworkError(raw, url) };
  } finally {
    clearTimeout(kill);
  }
}
